import { ASSET_BASE_URL } from './assets/getStaticAssetUrl';

import type { Manifest } from './manifest-fetcher';

export interface ImageVariant {
  width: number;
  hashedName: string;
  hash?: string;
  format?: string;
  sizeBytes?: number;
}

export interface RelatedAsset {
  role: string;
  normalizedName?: string;
  hashedName: string;
  hash?: string;
  folder?: string;
}

export interface ManifestEntry {
  hashedName: string;
  hash?: string;
  folder?: string;
  uploadedAt?: string;
  assetType?: string;
  relatedAssets?: RelatedAsset[];
  variants?: ImageVariant[];
}

const IMAGE_EXTENSION_REGEX = /\.(avif|webp|png|jpe?g|gif|svg)$/i;

const PUBLIC_ONLY_ASSETS = new Set([
  'stopwatch.svg',
  'payment-hands.svg',
  'treatment-hand.svg',
  'images/top-doctors-logo.png',
  'images/logo/lips-logo-favicon.png',
]);

const tryManifestLookup = (
  manifest: Manifest,
  name: string,
): ManifestEntry | undefined => {
  const entry = manifest[name];
  if (entry) return entry;
  if (IMAGE_EXTENSION_REGEX.test(name)) return undefined;
  return manifest[`${name}.webp`];
};

const toR2Url = (asset: ManifestEntry): string => {
  const folderPath = asset.folder ? `${asset.folder}/` : '';
  return `${ASSET_BASE_URL}/${folderPath}${asset.hashedName}`;
};

export function getAsset(manifest: Manifest, originalName: string): string {
  const fileName = originalName.split('/').pop() || '';
  const asset =
    tryManifestLookup(manifest, originalName) ??
    tryManifestLookup(manifest, fileName);

  if (!asset) {
    const cleanName = originalName.startsWith('/')
      ? originalName.slice(1)
      : originalName;
    if (
      process.env.NODE_ENV === 'development' &&
      !PUBLIC_ONLY_ASSETS.has(cleanName) &&
      !PUBLIC_ONLY_ASSETS.has(fileName)
    ) {
      console.warn(
        `Asset "${originalName}" not found in manifest. Check your spelling or run the uploader!`,
      );
    }
    return `/${originalName.startsWith('/') ? originalName.slice(1) : originalName}`;
  }

  return toR2Url(asset);
}

export function getRelatedAsset(
  manifest: Manifest,
  originalName: string,
  role: string,
): string | null {
  const fileName = originalName.split('/').pop() || '';
  const asset =
    tryManifestLookup(manifest, originalName) ??
    tryManifestLookup(manifest, fileName);

  if (!asset?.relatedAssets) return null;

  const related = asset.relatedAssets.find((r) => r.role === role);
  if (!related) return null;

  const folderPath = related.folder ? `${related.folder}/` : '';
  return `${ASSET_BASE_URL}/${folderPath}${related.hashedName}`;
}

export function getAssetSrcSet(
  manifest: Manifest,
  originalName: string,
): string | null {
  const fileName = originalName.split('/').pop() || '';
  const asset =
    tryManifestLookup(manifest, originalName) ??
    tryManifestLookup(manifest, fileName);
  if (!asset?.variants?.length) return null;

  const folderPath = asset.folder ? `${asset.folder}/` : '';
  const sorted = [...asset.variants].sort((a, b) => a.width - b.width);

  return sorted
    .map((v) => `${ASSET_BASE_URL}/${folderPath}${v.hashedName} ${v.width}w`)
    .join(', ');
}

export function getAssetForWidth(
  manifest: Manifest,
  originalName: string,
  targetWidth: number,
): string {
  const fileName = originalName.split('/').pop() || '';
  const asset =
    tryManifestLookup(manifest, originalName) ??
    tryManifestLookup(manifest, fileName);
  if (!asset?.variants?.length) return getAsset(manifest, originalName);

  const folderPath = asset.folder ? `${asset.folder}/` : '';
  const sorted = [...asset.variants].sort((a, b) => a.width - b.width);
  const fit = sorted.find((v) => v.width >= targetWidth);
  if (!fit) return getAsset(manifest, originalName);

  return `${ASSET_BASE_URL}/${folderPath}${fit.hashedName}`;
}

export function getVideoPosterUrl(videoUrl: string): string | undefined {
  if (!videoUrl) return undefined;
  if (!/\.mp4($|\?)/i.test(videoUrl)) return undefined;
  return videoUrl.replace(/\.mp4($|\?)/i, '-poster.webp$1');
}

export function getVideoAsset(
  manifest: Manifest,
  videoName: string,
): { videoUrl: string; posterUrl: string | null } {
  const videoUrl = getAsset(manifest, videoName);
  const posterUrl = getRelatedAsset(manifest, videoName, 'poster');
  return { videoUrl, posterUrl };
}
